/**
 * minCalc.js — algorithms for finding minimum investment to OHKO or survive.
 *
 * findMinOHKO: what's the cheapest config (EVs + nature + item) that guarantees OHKO?
 * findMinSurvive: what's the cheapest config that guarantees the defender survives?
 */
import { Move } from '@smogon/calc';
import { NATURE_TABLE } from '../data/commonSets';
import { getGen } from './showdownHelpers';
import { buildPokemon, buildField, calcStat } from './calcCore';
import { calculate } from '@smogon/calc';

// ── Helpers ────────────────────────────────────────────────────────────────────

function getDefHP(defCfg) {
  return calcStat('hp', defCfg.baseStats.hp, defCfg.evs.hp, defCfg.ivs.hp,
    defCfg.level, defCfg.nature);
}

function rollsMin(rolls) { return Math.min(...rolls); }
function rollsMax(rolls) { return Math.max(...rolls); }

function getMoveObj(gen, moveName, fieldConfig) {
  const genObj = getGen(gen);
  const mObj = new Move(genObj, moveName);
  if (fieldConfig?.attackerSide?.isCharged && mObj.type === 'Electric') mObj.bp *= 2;
  return mObj;
}

function doCalc(gen, atkCfg, defCfg, moveName, fieldCfg) {
  try {
    const genObj = getGen(gen);
    const aPokemon = buildPokemon(genObj, atkCfg);
    const dPokemon = buildPokemon(genObj, defCfg);
    const fieldObj = buildField(fieldCfg);
    const moveObj = getMoveObj(gen, moveName, fieldCfg);
    const result = calculate(genObj, aPokemon, dPokemon, moveObj, fieldObj);
    if (!Array.isArray(result.damage)) return null;
    return result.damage;
  } catch { return null; }
}

/** Returns true if ALL 16 rolls >= defHP (guaranteed OHKO). */
function isGuaranteedOHKO(rolls, defHP) {
  return rollsMin(rolls) >= defHP;
}

/** Returns true if ALL 16 rolls < defHP (guaranteed survival). */
function isGuaranteedSurvival(rolls, defHP) {
  return rollsMax(rolls) < defHP;
}

/** All natures that boost a given smogon stat key. */
function boostedNaturesFor(smogStatKey) {
  return Object.entries(NATURE_TABLE)
    .filter(([, v]) => v.boost === smogStatKey)
    .map(([name]) => name);
}

/** All natures that boost defense for a given move category. */
function defensiveNaturesFor(category) {
  const smogKey = category === 'Special' ? 'specialDefense' : 'defense';
  return boostedNaturesFor(smogKey);
}

// ── Min-OHKO ──────────────────────────────────────────────────────────────────

/**
 * Find the minimum attacker investment (EVs + nature + item) to guarantee OHKO.
 *
 * Returns one of:
 *   { success: true, alreadyOHKO: true, investment: 'Already OHKOs with current config' }
 *   { success: true, evs, nature, item, investment: '<human readable>' }
 *   { success: false, investment: 'Cannot OHKO', maxPct: '88.5' }
 */
export function findMinOHKO(gen, attackerConfig, defenderConfig, moveName, fieldConfig) {
  const genObj = getGen(gen);
  const moveRef = new Move(genObj, moveName);
  const category = moveRef.category;
  const offKey = category === 'Special' ? 'spa' : 'atk';
  const smogOffKey = offKey === 'atk' ? 'attack' : 'specialAttack';

  const defHP = getDefHP(defenderConfig);
  const currentNature = attackerConfig.nature || 'Hardy';

  // Items to try, in cost-preference order (fewer is better — items are expensive)
  const itemOrder = category === 'Special'
    ? ['None', 'Life Orb', 'Choice Specs', 'Wise Glasses']
    : ['None', 'Life Orb', 'Choice Band', 'Muscle Band'];

  // Natures to try (current first, then beneficial)
  const natureOrder = [currentNature, ...new Set(boostedNaturesFor(smogOffKey))];

  let best = null; // { evs, nature, item, cost }

  for (const item of itemOrder) {
    for (const nature of natureOrder) {
      for (let ev = 0; ev <= 252; ev += 4) {
        const cfg = {
          ...attackerConfig,
          item: item === 'None' ? 'None' : item,
          nature,
          evs: { ...attackerConfig.evs, [offKey]: ev },
        };
        const rolls = doCalc(gen, cfg, defenderConfig, moveName, fieldConfig);
        if (!rolls) continue;
        if (isGuaranteedOHKO(rolls, defHP)) {
          // Cost metric: items are most expensive, then nature change, then EVs
          const cost = (item !== 'None' ? 10000 : 0)
                     + (nature !== currentNature ? 500 : 0)
                     + ev;
          if (!best || cost < best.cost) {
            const isCurrentConfig =
              item === (attackerConfig.item || 'None') &&
              nature === currentNature &&
              ev === (attackerConfig.evs?.[offKey] ?? 0);
            best = { evs: ev, nature, item, cost, isCurrentConfig };
          }
          break; // found minimum EVs for this item/nature; stop increasing EVs
        }
      }
    }
  }

  if (best) {
    if (best.isCurrentConfig) {
      return { success: true, alreadyOHKO: true,
               investment: 'Already OHKOs with current config',
               evs: best.evs, nature: best.nature, item: best.item };
    }
    const parts = [];
    const baseEV = attackerConfig.evs?.[offKey] ?? 0;
    if (best.evs !== baseEV) parts.push(`${best.evs} ${offKey.toUpperCase()} EVs`);
    if (best.nature !== currentNature) parts.push(`${best.nature} nature`);
    if (best.item !== 'None' && best.item !== (attackerConfig.item || 'None'))
      parts.push(best.item);
    return { success: true, evs: best.evs, nature: best.nature, item: best.item,
             investment: parts.length ? parts.join(' + ') : 'current config' };
  }

  // Cannot OHKO — show how close max investment gets
  const maxCfg = {
    ...attackerConfig,
    item: itemOrder[itemOrder.length - 1],
    nature: natureOrder.find(n => n !== currentNature) || currentNature,
    evs: { ...attackerConfig.evs, [offKey]: 252 },
  };
  const maxRolls = doCalc(gen, maxCfg, defenderConfig, moveName, fieldConfig);
  const maxPctStr = maxRolls
    ? ((rollsMax(maxRolls) / defHP) * 100).toFixed(1)
    : '?';
  return { success: false, investment: 'Cannot OHKO (even with max investment)',
           maxPct: maxPctStr };
}

// ── Min-Survive ───────────────────────────────────────────────────────────────

/**
 * Find the minimum defender investment (EVs + nature + item) to guarantee survival.
 *
 * Returns one of:
 *   { success: true, alreadySurvives: true, investment: 'Already survives' }
 *   { success: true, hpEV, defEV, nature, item, investment: '<human readable>' }
 *   { success: false, investment: 'Cannot guarantee survival' }
 */
export function findMinSurvive(gen, defenderConfig, attackerConfig, moveName, fieldConfig) {
  const genObj = getGen(gen);
  const moveRef = new Move(genObj, moveName);
  const category = moveRef.category;
  const defStatKey = category === 'Special' ? 'spd' : 'def';
  const currentNature = defenderConfig.nature || 'Hardy';

  const checkSurvives = (defCfg) => {
    const rolls = doCalc(gen, attackerConfig, defCfg, moveName, fieldConfig);
    if (!rolls) return false;
    const hp = getDefHP(defCfg);
    return isGuaranteedSurvival(rolls, hp);
  };

  if (checkSurvives(defenderConfig)) {
    return { success: true, alreadySurvives: true, investment: 'Already survives with current config' };
  }

  // Items to try (in cost-preference order)
  const itemOrder = ['None', 'Eviolite', 'Assault Vest', 'Leftovers'];
  // Natures that boost the relevant defensive stat
  const natureOrder = [currentNature, ...new Set(defensiveNaturesFor(category))];

  let best = null;

  for (const item of itemOrder) {
    for (const nature of natureOrder) {
      // Try increasing HP EVs first (often most efficient)
      for (let hpEV = 0; hpEV <= 252; hpEV += 4) {
        const cfg = {
          ...defenderConfig,
          item: item === 'None' ? 'None' : item,
          nature,
          evs: { ...defenderConfig.evs, hp: hpEV },
        };
        if (checkSurvives(cfg)) {
          const cost = (item !== 'None' ? 10000 : 0)
                     + (nature !== currentNature ? 500 : 0)
                     + hpEV;
          if (!best || cost < best.cost) {
            best = { hpEV, defEV: defenderConfig.evs?.[defStatKey] ?? 0, nature, item, cost };
          }
          break;
        }
      }

      // Try increasing the defensive stat EVs with current HP investment
      const baseHP = defenderConfig.evs?.hp ?? 0;
      for (let defEV = 0; defEV <= 252; defEV += 4) {
        const cfg = {
          ...defenderConfig,
          item: item === 'None' ? 'None' : item,
          nature,
          evs: { ...defenderConfig.evs, hp: baseHP, [defStatKey]: defEV },
        };
        if (checkSurvives(cfg)) {
          const cost = (item !== 'None' ? 10000 : 0)
                     + (nature !== currentNature ? 500 : 0)
                     + baseHP + defEV;
          if (!best || cost < best.cost) {
            best = { hpEV: baseHP, defEV, nature, item, cost };
          }
          break;
        }
      }
    }
  }

  if (best) {
    const parts = [];
    const baseHP = defenderConfig.evs?.hp ?? 0;
    const baseDef = defenderConfig.evs?.[defStatKey] ?? 0;
    if (best.hpEV !== baseHP) parts.push(`${best.hpEV} HP EVs`);
    if (best.defEV !== baseDef) parts.push(`${best.defEV} ${defStatKey.toUpperCase()} EVs`);
    if (best.nature !== currentNature) parts.push(`${best.nature} nature`);
    if (best.item !== 'None' && best.item !== (defenderConfig.item || 'None'))
      parts.push(best.item);
    return {
      success: true,
      hpEV: best.hpEV, defEV: best.defEV, nature: best.nature, item: best.item,
      investment: parts.length ? parts.join(' + ') : 'current config',
    };
  }

  return { success: false, investment: 'Cannot guarantee survival even with max investment' };
}
